/**
 * The reusable prompt — paste into an empty project and get this pipeline.
 *
 * Written out of the working implementation rather than from memory, so the
 * endpoints, model names and magic numbers are the ones that are actually in
 * use. The parts worth having are not the API calls, which anyone can look up;
 * they are the constraints that took a run each to discover — that the rigger
 * 422s on chibi proportions unless the arms are pushed clear of the torso, that
 * the front view has to be fed back in as a reference for the other two, that
 * placing joints from a fixed table puts the elbow 18.7cm from the flesh it
 * drives.
 *
 * Kept as one exported string rather than assembled from parts: it exists to be
 * copied in full, and anything that makes it harder to read as a whole is
 * working against that.
 *
 * Mirrors docs/pipeline-prompt.md in the project repo.
 */
export const PIPELINE_PROMPT = `Build me a pipeline that turns a photograph of a person into a rigged, animated
3D character as a GLB, using gpt-image-2 for stylisation and Meshy for
reconstruction and rigging, with a local Blender pass at the end.

I do not know 3D. Explain nothing about the theory; just build it, and stop at
each stage so I can look at the output before paying for the next one.

## Shape of the thing

Four stages, each a separate command, each resumable and idempotent. Skip a
stage whose output already exists unless I pass --redo:

  stylize <photo.jpg> --name Ted    one photo   -> three stylised views
  model   ted                       three views -> textured mesh
  rig     ted                       mesh        -> skinned character
  finish  ted                       rigged      -> the deliverable GLB

Separate commands is the whole point, not an inconvenience. Every stage is a
paid call and each one silently poisons the ones after it: a stylised view with
long hair or fused trouser legs produces a mesh that rigs badly, and nothing
downstream recovers it. Add an "all" command that chains them for when the
inputs are already trusted.

Keep run state in runs/<slug>/run.json. This matters for one non-obvious
reason: Meshy rigs from a task id, not from a file, so the id returned by the
model stage has to survive until the rig stage runs.

## Stage 1 — stylize

Call the OpenAI images EDITS endpoint, not generations. Model gpt-image-2. It
accepts up to 16 reference images in the image array. Omit input_fidelity
entirely, because the model always processes inputs at high fidelity and
passing it is an error.

Size 2144x3824. The constraints are: both edges a multiple of 16, at most 3:1,
and between 655,360 and 8,294,400 total pixels. This is essentially the largest
portrait it will produce. Note it is past OpenAI's stated reliability boundary
of 3,686,400 pixels, so expect the occasional view to miss the pose and need a
redo. That is what the review gate is for.

This size is the ceiling on how detailed the avatar can ever be. Meshy bakes its
texture out of these images, so no texture resolution downstream can recover
detail that was never in them.

Generate three views: front, then side, then back. Generate the front FIRST,
then pass the finished front image back in as a reference for the other two.
That feedback is the only reason all three come out as recognisably the same
person. Without it you get a mesh that is a blend of three slightly different
people.

The prompt for each view has to satisfy three masters at once, and the rigger is
the strictest:

  - Meshy documents auto-rigging as unsuitable for humanoid assets with unclear
    limb and body structure, and runs a pose estimation pass that returns 422
    when it cannot find a skeleton. A chibi style — head close to two fifths of
    total height, stubby limbs, mitten hands, no neck — is squarely inside that
    failure mode. Pose and limb separation are the only levers you have on it,
    so state them as hard requirements, not suggestions.

  - Use a wide A-pose: both arms straight, angled roughly 55 degrees down and
    out from the shoulders. The arms are short, so push them out far enough that
    a clear band of plain background is visible between each arm and the side of
    the torso along the arm's whole length. Offer a strict T-pose as an
    alternative.

  - The three views must be the same character at the same scale, framing and
    camera height.

Split the face instructions into two separate lists, because collapsing them
breaks one or the other:

  - HOW IT IS DRAWN is style: solid glossy eye with one highlight, brows as
    smooth masses, teeth as a band. Identical for every character, or two
    avatars made a fortnight apart do not belong on the same shelf. This
    overrides the photograph wherever they disagree.

  - WHAT COMES FROM THE PERSON is the shape of the features: eye shape, spacing
    and tilt, nose width, how far the ears stand out. With hair and clothes
    stripped off, that is all a viewer has left to recognise someone by.

The first version of this had only the first list and described what varied as
"face shape and proportion" without saying which features that covered. Every
face converged on the same generic one. The second list is the fix.

Take an --outfit flag and put it in the prompt, so the generated likeness wears
what you intend from the start.

## Stage 2 — model

POST to https://api.meshy.ai/openapi/v1/multi-image-to-3d with all three views.
Use the multi-image endpoint, not image-to-3d — the back and sides then come
from real input instead of being invented. Under ai_model meshy-7 the FIRST
image in the array is treated as the primary front view, so ordering matters.

Data URIs are accepted anywhere an image URL is documented, so this works from
localhost with no public tunnel.

Body:

  ai_model            meshy-7
  pose_mode           matches what you asked for in stage 1
  symmetry_mode       auto
  should_texture      true
  enable_pbr          true
  texture_resolution  4k
  topology            triangle
  should_remesh       true
  target_polycount    250000

On those last four. Base colour is the only map that survives rigging, so it
carries nearly all the perceived detail and deserves 4k; PBR is still worth
asking for because the pre-rig artifact is what you review. Remeshing is what
keeps the face count under the rigger's hard 300,000 ceiling — its own API error
is "TargetPolycount must be 300,000 or less" — and a rig that 422s costs you the
whole mesh generation. Quad topology is the conventional choice for an organic
character and deforms better, but it is unclear whether that 300k budget counts
quads or triangles; if it counts quads, asking for 250,000 is half a million
triangles and the generation is wasted. Start on triangles.

Poll the task until SUCCEEDED. Retry 429 and 5xx with backoff.

## Stage 3 — rig

POST to /rigging with input_task_id (the model task id, not a file) and
height_meters 1.7. The rigger requires a textured humanoid GLB whose face
points down +Z.

## Stage 4 — finish, locally in Blender

Two ways into Blender here, and you want both, for different jobs.

Use the Blender MCP while you are writing this stage. It talks to a Blender that
is already running, so you can load an avatar into a live scene, try something,
and actually look at what happened. Developing against geometry you cannot see
is the slowest possible way to do this, and every defect in the list below was
found by looking.

Then have the pipeline invoke the same script headless, with --background
--python. That path is reproducible, runs unattended, and does not care whether
a window is open. The rule that falls out: anything that produces a file goes
through the headless script; the MCP is for inspecting and driving the live
session.

Do the work in Blender rather than a glTF library either way, because rebinding
a skinned mesh to a new skeleton, repairing weights and editing a texture atlas
are all things it does natively; doing it against raw glTF buffers is a lot of
code whose failure mode is a subtly broken rig. Write only into the run's own
directory so a failure can be retried without cleanup.

1. Normalise the transform: 1.75 units tall, soles on the floor, centred, no
   scale left on the armature. Meshy wraps its rigs in an armature scaled to
   0.01, which will otherwise report your avatar as 1.7cm tall.

2. Rebuild the skeleton with canonical NAMES and HIERARCHY, but place each joint
   where the provider put it on this character. Bind by vertex-group name.

   Placing joints from a fixed table instead is the trap. A table holds one
   donor's T-pose proportions, and the provider generates in an A-pose, so the
   arms hang at 45 degrees while the table's arm chain runs out horizontally.
   Measured: elbow 18.7cm and wrist 28.5cm away from the flesh they drive,
   against 7.0cm and 5.9cm for the provider's own rig. Every clip that bends an
   elbow folds the forearm in the wrong place.

   Shared names and hierarchy are what actually matter. Clips bind by name and
   retarget against rest pose, so they play on any proportions.

3. Repair the cross-leg weight bleed. Proximity-based auto-rigging always
   produces it on wide trousers.

4. Drop the provider's normal map when it is noise rather than detail — measure
   it, do not guess. Rigging discards the normal and roughness maps anyway.

5. Paint finger separations into the texture atlas and tighten the wrist
   weights, so the mitten reads as a hand. Do NOT graft authored finger
   geometry on by default: fingers on a mitten-proportioned palm read as a
   bunch of bananas.

6. Rig the eyebrows. It is the only facial articulation these characters can
   have — they arrive with no blendshapes and no face bones, but the brows are
   real geometry standing about 9mm proud of the skin, so a bone can move them.

## Stage 5 — animate

POST to /animations with rig_task_id and an action_id. Because every character
came out of stage 4 with the same bone names and hierarchy, one clip drives all
of them with no retargeting.

## What NOT to do

Do not try to build one generic body that each person is applied to as a skin.
It is the obvious optimisation and it does not work: projecting one person's
face onto another person's sculpted head gives two mouths, ghost eyes and
imprints on the cheeks, because the head underneath already has a face modelled
and painted into it. Keep the mesh the provider generated — it is the person, at
full detail, with their clothes — and replace only the skeleton and the weights.

## Judging the output

Build a scoring script rather than reviewing by eye, because "the face looks
blurry" cannot tell a regression from a bad screenshot. Turn each complaint into
a number:

  - Atlas utilisation. Fragmented UVs are the real cause of blurriness. Smart UV
    Project on a voxel remesh once filled 32% of a 4096 atlas and left the rest
    gutter, giving a "4k" model less effective resolution than the 4k source.
  - Texel density, in texels per cm of surface. This is the number that means
    "blurry".
  - Speckle: isolated dark texels inside skin, from bake margins bleeding hair
    islands into skin islands.
  - Metallic outliers: gold patches where junk UVs sample the atlas origin.
  - Which maps are present.

Set explicit thresholds as targets to build against.

## Practical

Node with TypeScript, run via tsx. Blender installed locally for stage 4, plus
the Blender MCP configured if you want the interactive half of it.

Keys in .env as OPENAI_API_KEY and
MESHY_API_KEY — load the env file before anything reads process.env, since
config parsed at module-evaluation time will otherwise be built from an empty
environment and report a missing key that is sitting right there.

Give me a mock provider mode so I can exercise the whole flow without spending
anything.
`
